import { useState } from "react";
import MeetingRecorder from "@/components/MeetingRecorder";
import { ArrowClockwise } from "@phosphor-icons/react";

/**
 * BriefingAudioStrip — the single canonical audio-capture surface used by
 * both the Briefing Studio card list (inline, always visible on newly
 * created briefings) and the BriefingReviewDialog SourcePane.
 *
 * Reuses the existing MeetingRecorder pipeline (which posts to
 * /notes/{id}/audio → Whisper) so all four briefing kinds share identical
 * recording behavior. When audio already exists, renders playback with a
 * "Record again · replace" affordance that remounts the recorder without
 * discarding the transcript until the new recording finishes.
 */
export default function BriefingAudioStrip({ note, onChanged, compact = false }) {
  const [replacing, setReplacing] = useState(false);
  // Iter 85.31 · Hard guard against a null/undefined note. The parent
  // (BriefingReviewDialog SourcePane) can briefly hand us `current === null`
  // if a post-apply refetch races or returns an empty body — see
  // BriefingReviewDialog::applyIds. Rendering nothing in that case keeps
  // the whole dialog alive so the partner can retry or navigate away
  // instead of hitting a full React crash.
  if (!note || !note.id) return null;
  const hasAudio = !!note?.meeting_audio_key;
  const showRecorder = !hasAudio || replacing;

  const handleTranscribed = (data) => {
    onChanged?.({ ...note, ...data });
    setReplacing(false);
  };

  return (
    <div className={compact ? "space-y-2" : "space-y-3"} data-testid={`briefing-audio-strip-${note?.id}`}>
      {showRecorder && (
        <>
          <MeetingRecorder noteId={note.id} onTranscribed={handleTranscribed} />
          {replacing && (
            <button
              type="button"
              onClick={() => setReplacing(false)}
              data-testid={`briefing-cancel-replace-${note.id}`}
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
            >
              ← Cancel replace · keep existing recording
            </button>
          )}
        </>
      )}
      {hasAudio && !replacing && (
        <div className="space-y-2">
          <audio
            controls
            src={`${process.env.REACT_APP_BACKEND_URL}/api/notes/${note.id}/audio-stream?_=${note.meeting_transcribed_at || ""}`}
            className="w-full"
            data-testid={`briefing-audio-player-${note.id}`}
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              {note.meeting_transcript_lang || "?"} · {note.meeting_audio_duration_seconds ? Math.round(note.meeting_audio_duration_seconds) + "s" : "—"} · auto-deletes {note.meeting_audio_expires_at ? new Date(note.meeting_audio_expires_at).toLocaleDateString() : "in 30d"}
            </div>
            <button
              type="button"
              onClick={() => setReplacing(true)}
              data-testid={`briefing-replace-audio-${note.id}`}
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1"
            >
              <ArrowClockwise size={11} weight="bold" /> Record again · replace
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
